import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { SortOrder } from 'primereact/api';
import { QRCodeSVG } from 'qrcode.react';
import findFatura from '../../service/findFatura';
import gerarPdfFatura from '../../service/gerarPdfFatura';
import gerarNotaFiscalFatura from '../../service/gerarNotaFiscalFatura';
import { useGlobalState } from '../../components/globalState/GlobalStateProvider';
import './FaturasHome.css';

type StatusInfo = { status: string; label: string };

const STATUS_THEME: { [key: string]: { className: string; icon: string } } = {
  PAGO: { className: 'fh-status--pago', icon: 'pi pi-check-circle' },
  VENCIDO: { className: 'fh-status--vencido', icon: 'pi pi-exclamation-circle' },
  RECEBIVEL: { className: 'fh-status--recebivel', icon: 'pi pi-clock' },
};

export default function FaturasHome() {
  const { t } = useTranslation();
  const { state } = useGlobalState();
  const [faturas, setFaturas] = useState<any[]>([]);
  const [pixFatura, setPixFatura] = useState<any>(null);
  const toast = useRef<Toast>(null);

  const funcoes: string[] = state?.user?.funcoes || [];
  const podeBoleto = funcoes.includes('FATURA_PDF');
  const podeNota = funcoes.includes('FATURA_GERAR_NOTA_FISCAL');

  const copiarPix = (codigo: string) => {
    navigator.clipboard.writeText(codigo);
    toast.current?.show({ severity: 'success', summary: 'Copiado!', detail: 'Código PIX copiado', life: 2000 });
  };

  useEffect(() => {
    const STATUS_PERMITIDOS = ['PDF_GERADO', 'PAGO'];
    const lazyState = {
      first: 0,
      rows: 20,
      page: 1,
      faturamento: 'null',
      cliente: 'null',
      status: 'null',
      sortField: 'dataReferencia',
      sortOrder: SortOrder.DESC,
    };
    findFatura(lazyState)
      .then((data) => {
        const lista = Array.isArray(data?.faturas) ? data.faturas : [];
        const filtradas = lista
          .filter((f: any) => STATUS_PERMITIDOS.includes(f.statusFatura))
          .slice(0, 3);
        setFaturas(filtradas);
      })
      .catch(() => setFaturas([]));
  }, []);

  const computeStatus = (fatura: any): StatusInfo => {
    let status = fatura.statusFatura || 'INDEFINIDO';
    let label = fatura.statusFaturaLabel || '—';
    if (fatura.dataVencimento && status === 'PDF_GERADO') {
      const [dia, mes, ano] = String(fatura.dataVencimento).split('/').map(Number);
      const vencimento = new Date(ano, mes - 1, dia);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      if (vencimento < hoje) {
        status = 'VENCIDO';
        label = 'Vencido';
      } else {
        status = 'RECEBIVEL';
        label = 'Em aberto';
      }
    }
    return { status, label };
  };

  const formatDate = (value: { toLocaleDateString: (arg0: string, arg1: { day: string; month: string; year: string; }) => any; }) => {
    return value.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
  };

  const baixarArquivo = (blobFile: Blob, nome: string) => {
    const url = window.URL.createObjectURL(new Blob([blobFile], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nome);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadBoleto = (fatura: any) => {
    gerarPdfFatura(fatura.id)
      .then((blobFile: Blob) => baixarArquivo(blobFile, `boleto_${fatura.numero || fatura.id}.pdf`))
      .catch(() => toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Erro ao baixar o boleto', life: 4000 }));
  };

  const downloadNota = (fatura: any) => {
    gerarNotaFiscalFatura(fatura.id)
      .then((blobFile: Blob) => baixarArquivo(blobFile, `nota_fiscal_${fatura.numero || fatura.id}.pdf`))
      .catch(() => toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Erro ao baixar a nota fiscal', life: 4000 }));
  };

  if (faturas.length === 0) {
    return null;
  }

  const headerTemplate = (
    <div className="fh-panel-header">
      <i className="pi pi-dollar" />
      <span>{t('faturas')}</span>
      <span className="fh-panel-count">{faturas.length}</span>
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Panel header={headerTemplate} style={{ width: '99%' }} className="fh-panel">
        <div className="fh-grid">
          {faturas.map((fatura: any) => {
            const { status, label } = computeStatus(fatura);
            const theme = STATUS_THEME[status] || { className: 'fh-status--neutro', icon: 'pi pi-file' };
            return (
              <div className={`fh-card fh-card--${status.toLowerCase()}`} key={fatura.id}>
                <div className="fh-card-top">
                  <div className="fh-card-numero">
                    <span className="fh-card-numero-label">{t('fatura')}</span>
                    <span className="fh-card-numero-value">Nº {fatura.numero || fatura.id}</span>
                  </div>
                  <span className={`fh-status ${theme.className}`}>
                    <i className={theme.icon} />
                    {label}
                  </span>
                </div>

                <div className="fh-card-valor">
                  <span className="fh-card-valor-moeda">R$</span>
                  <span className="fh-card-valor-numero">{fatura.valor}</span>
                </div>

                <div className="fh-card-info">
                  <div className="fh-card-info-item">
                    <i className="pi pi-calendar" />
                    <div>
                      <span className="fh-card-info-label">{t('dataVencimento')}</span>
                      <span className="fh-card-info-value">{fatura.dataVencimento}</span>
                    </div>
                  </div>
                  {status === 'PAGO' && fatura.dataPagamento && (
                    <div className="fh-card-info-item">
                      <i className="pi pi-check" />
                      <div>
                        <span className="fh-card-info-label">{t('dataPagamento')}</span>
                        <span className="fh-card-info-value">{fatura.dataPagamento ? formatDate(new Date(fatura.dataPagamento)) : ''}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="fh-card-actions">
                  {fatura.qrCodePix && status !== 'PAGO' && (
                    <Button
                      label="PIX"
                      icon="pi pi-qrcode"
                      className="fh-btn fh-btn--pix"
                      onClick={() => setPixFatura(fatura)}
                    />
                  )}
                  {podeBoleto && (
                    <Button
                      label="Boleto"
                      icon="pi pi-barcode"
                      className="fh-btn fh-btn--boleto"
                      onClick={() => downloadBoleto(fatura)}
                      disabled={!fatura.linhaDigitavel}
                      tooltip={!fatura.linhaDigitavel ? 'Boleto indisponível' : undefined}
                    />
                  )}
                  {podeNota && (
                    <Button
                      label="Nota"
                      icon="pi pi-file"
                      className="fh-btn fh-btn--nota"
                      onClick={() => downloadNota(fatura)}
                      disabled={!fatura.possuiNotaFiscal}
                      tooltip={!fatura.possuiNotaFiscal ? 'Nota fiscal indisponível' : undefined}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      <Dialog
        visible={!!pixFatura}
        onHide={() => setPixFatura(null)}
        header={null}
        showHeader={false}
        style={{ width: '380px' }}
        contentClassName="fh-pix-dialog-content"
        modal
        dismissableMask
      >
        {pixFatura && (
          <div className="fh-pix-modal">
            <button className="fh-pix-modal-close" onClick={() => setPixFatura(null)} aria-label="Fechar">
              <i className="pi pi-times" />
            </button>
            <div className="fh-pix-modal-head">
              <i className="pi pi-qrcode" />
              <span>Pagar com PIX</span>
            </div>
            <div className="fh-pix-modal-valor">
              <span className="fh-pix-modal-valor-label">Fatura Nº {pixFatura.numero || pixFatura.id}</span>
              <span className="fh-pix-modal-valor-num">R$ {pixFatura.valor}</span>
            </div>
            <div className="fh-pix-modal-qr">
              <QRCodeSVG value={pixFatura.qrCodePix} size={210} level="H" includeMargin={true} />
            </div>
            <span className="fh-pix-modal-hint">Abra o app do seu banco e escaneie o QR Code</span>
            <Button
              label="Copiar código PIX"
              icon="pi pi-copy"
              className="fh-pix-modal-copy"
              onClick={() => copiarPix(pixFatura.qrCodePix)}
            />
          </div>
        )}
      </Dialog>
      <br />
    </>
  );
}
